import React, { useState, useEffect } from 'react';
import { useNotification } from './NotificationSystem';
import { API_URL } from '../config';
import './GamingHub.css';

function GamingHub({ user, servers, onJoinServer, onSelectServer, socket, onClose }) {
  const { showSuccess, showError, showInfo } = useNotification();
  const [activeView, setActiveView] = useState('now');
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [gameActivity, setGameActivity] = useState({});
  const [partyMembers, setPartyMembers] = useState([]);

  useEffect(() => {
    // Fetch gaming activity data
    fetchGamingActivity();
    
    // Listen for game status updates
    if (socket) {
      socket.on('game_status_update', handleGameStatusUpdate);
      socket.on('party_update', handlePartyUpdate);
      
      return () => {
        socket.off('game_status_update', handleGameStatusUpdate);
        socket.off('party_update', handlePartyUpdate);
      };
    }
  }, [socket]);

  const fetchGamingActivity = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/gaming/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnlineMembers(data.onlineMembers || []);
        setGameActivity(data.gameActivity || {});
      }
    } catch (error) {
      console.error('Error fetching gaming activity:', error);
    }
  };

  const handleGameStatusUpdate = (data) => {
    // Update game activity in real-time
    setGameActivity(prev => ({
      ...prev,
      [data.game]: [...(prev[data.game] || []), data]
    }));
  };

  const handlePartyUpdate = (data) => {
    setPartyMembers(data.members);
  };

  const getPlayingGame = (member) => {
    // In a real implementation, this would check member's current game status
    // For now, we'll use a placeholder
    return member.currentGame || null;
  };

  const getServersByGame = () => {
    const gameServers = {};
    
    servers.forEach(server => {
      // Group servers by the games being played in them
      const game = server.primaryGame || 'General';
      if (!gameServers[game]) {
        gameServers[game] = [];
      }
      gameServers[game].push(server);
    });
    
    return gameServers;
  };

  const renderNowView = () => {
    const gameServers = getServersByGame();
    
    return (
      <div className="gaming-now">
        {/* Active Party Section */}
        <div className="gaming-section party-section">
          <div className="section-header">
            <h2>🎮 Your Party</h2>
            {partyMembers.length > 0 && (
              <span className="party-count">{partyMembers.length} in party</span>
            )}
          </div>
          
          {partyMembers.length === 0 ? (
            <div className="empty-state">
              <p>No active party</p>
              <button className="btn-create-party">Create Party</button>
            </div>
          ) : (
            <div className="party-members">
              {partyMembers.map(member => (
                <div key={member.id} className="party-member">
                  <div className="member-avatar">
                    {member.avatar_url ? (
                      <img src={`${API_URL}${member.avatar_url}`} alt={member.username} />
                    ) : (
                      <div className="avatar-placeholder">{member.username[0]}</div>
                    )}
                    <span className="status-indicator online"></span>
                  </div>
                  <div className="member-info">
                    <div className="member-name">{member.username}</div>
                    <div className="member-game">{member.currentGame || 'Online'}</div>
                  </div>
                  <div className="member-voice">
                    {member.inVoice && <span className="voice-active">🎤</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Game Hubs Section */}
        <div className="gaming-section game-hubs">
          <div className="section-header">
            <h2>🎯 Active Game Sessions</h2>
          </div>
          
          <div className="game-sessions">
            {Object.entries(gameServers).map(([game, serverList]) => (
              <div key={game} className="game-hub-card">
                <div className="game-hub-header">
                  <h3>{game}</h3>
                  <span className="active-count">
                    {serverList.reduce((acc, s) => acc + (s.activeMembers || 0), 0)} playing
                  </span>
                </div>
                
                <div className="game-hub-servers">
                  {serverList.map(server => (
                    <div 
                      key={server.id} 
                      className="hub-server-item"
                      onClick={() => onSelectServer(server)}
                    >
                      <div className="server-info">
                        <div className="server-icon">
                          {server.avatar_url ? (
                            <img src={`${API_URL}${server.avatar_url}`} alt={server.name} />
                          ) : (
                            <div className="icon-placeholder">{server.name[0]}</div>
                          )}
                        </div>
                        <div className="server-details">
                          <div className="server-name">{server.name}</div>
                          <div className="server-stats">
                            {server.activeMembers || 0} online • {server.inVoice || 0} in voice
                          </div>
                        </div>
                      </div>
                      <button className="btn-quick-join">Quick Join</button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            
            {Object.keys(gameServers).length === 0 && (
              <div className="empty-state">
                <p>No active game sessions</p>
                <p className="empty-hint">Join a server or create a party to get started</p>
              </div>
            )}
          </div>
        </div>

        {/* Online Friends Playing */}
        <div className="gaming-section friends-playing">
          <div className="section-header">
            <h2>👥 Friends Playing</h2>
          </div>
          
          <div className="friends-list">
            {onlineMembers.filter(m => m.id !== user.id).map(member => {
              const game = getPlayingGame(member);
              return (
                <div key={member.id} className="friend-card">
                  <div className="friend-avatar">
                    {member.avatar_url ? (
                      <img src={`${API_URL}${member.avatar_url}`} alt={member.username} />
                    ) : (
                      <div className="avatar-placeholder">{member.username[0]}</div>
                    )}
                    <span className={`status-indicator ${member.status}`}></span>
                  </div>
                  <div className="friend-info">
                    <div className="friend-name">{member.username}</div>
                    <div className="friend-activity">
                      {game ? `🎮 ${game}` : '💬 Online'}
                    </div>
                  </div>
                  <div className="friend-actions">
                    <button className="btn-icon" title="Invite to Party">+</button>
                    <button className="btn-icon" title="Message">💬</button>
                  </div>
                </div>
              );
            })}
            
            {onlineMembers.length === 0 && (
              <div className="empty-state">
                <p>No friends online</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderServersView = () => {
    const gameServers = getServersByGame();
    
    return (
      <div className="gaming-servers-view">
        <div className="servers-header">
          <h2>Your Gaming Communities</h2>
          <p>Servers organized by game activity</p>
        </div>
        
        {Object.entries(gameServers).map(([game, serverList]) => (
          <div key={game} className="game-category">
            <div className="category-header">
              <h3>🎮 {game}</h3>
              <span>{serverList.length} {serverList.length === 1 ? 'server' : 'servers'}</span>
            </div>
            
            <div className="servers-grid">
              {serverList.map(server => (
                <div 
                  key={server.id} 
                  className="server-card"
                  onClick={() => onSelectServer(server)}
                >
                  <div className="server-card-icon">
                    {server.avatar_url ? (
                      <img src={`${API_URL}${server.avatar_url}`} alt={server.name} />
                    ) : (
                      <div className="icon-placeholder-large">{server.name[0]}</div>
                    )}
                  </div>
                  <div className="server-card-info">
                    <h4>{server.name}</h4>
                    <div className="server-card-stats">
                      <span>👥 {server.activeMembers || 0} online</span>
                      <span>🎤 {server.inVoice || 0} in voice</span>
                    </div>
                  </div>
                  <div className="server-card-action">
                    <button className="btn-join-server">Join</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderQuickActionsBar = () => {
    return (
      <div className="quick-actions-bar">
        <button className="quick-action" onClick={() => showInfo('Party system coming soon!')}>
          <span className="action-icon">🎮</span>
          <span className="action-label">Create Party</span>
        </button>
        <button className="quick-action" onClick={() => showInfo('LFG system coming soon!')}>
          <span className="action-icon">🔍</span>
          <span className="action-label">Find Group</span>
        </button>
        <button className="quick-action" onClick={() => showInfo('Clips coming soon!')}>
          <span className="action-icon">🎬</span>
          <span className="action-label">Recent Clips</span>
        </button>
        <button className="quick-action" onClick={() => showInfo('Tournament mode coming soon!')}>
          <span className="action-icon">🏆</span>
          <span className="action-label">Tournaments</span>
        </button>
      </div>
    );
  };

  return (
    <div className="gaming-hub">
      <div className="gaming-hub-header">
        <div className="hub-title">
          <h1>⚡ Gaming Hub</h1>
          <p>Built for gamers, by gamers</p>
        </div>
        
        <div className="hub-nav">
          <button 
            className={`nav-tab ${activeView === 'now' ? 'active' : ''}`}
            onClick={() => setActiveView('now')}
          >
            🎮 Now Playing
          </button>
          <button 
            className={`nav-tab ${activeView === 'servers' ? 'active' : ''}`}
            onClick={() => setActiveView('servers')}
          >
            🏠 My Communities
          </button>
          <button 
            className={`nav-tab ${activeView === 'discover' ? 'active' : ''}`}
            onClick={() => setActiveView('discover')}
          >
            🔍 Discover
          </button>
        </div>

        <button className="hub-close-btn" onClick={onClose} title="Close Gaming Hub">
          ✕
        </button>
      </div>

      {renderQuickActionsBar()}

      <div className="gaming-hub-content">
        {activeView === 'now' && renderNowView()}
        {activeView === 'servers' && renderServersView()}
        {activeView === 'discover' && (
          <div className="discover-view empty-state">
            <h2>🔍 Discover New Communities</h2>
            <p>Find gaming communities, join tournaments, and connect with players</p>
            <p className="empty-hint">Coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GamingHub;
